"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAuthenticationService = void 0;
const vue_1 = require("vue");
const axios_1 = __importDefault(require("axios"));
const injection_symbols_1 = require("./injection-symbols");
const lifecycle_hooks_1 = require("./lifecycle-hooks");
const uuid_1 = require("uuid");
/* istanbul ignore next */
function getFromSessionStorage(key) {
    try {
        if (window.sessionStorage) {
            const storage = JSON.parse(window.sessionStorage.getItem('myHealthToolkitAuthStorage') || '{}');
            return storage[key];
        }
    }
    catch (error) { /* ignore */ }
    return null;
}
/* istanbul ignore next */
function storeInSessionStorage(key, value) {
    if (window.sessionStorage) {
        const storage = JSON.parse(window.sessionStorage.getItem('myHealthToolkitAuthStorage') || '{}');
        storage[key] = value;
        window.sessionStorage.setItem('myHealthToolkitAuthStorage', JSON.stringify(storage));
    }
}
/* istanbul ignore next */
function resetSessionStorage() {
    try {
        if (window.sessionStorage) {
            window.sessionStorage.removeItem('myHealthToolkitAuthStorage');
        }
    }
    catch (error) { /* ignore */ }
}
function createAuthenticationService(options) {
    var _a;
    let { commwebServiceBaseUrl, ledsServiceBaseUrl, ledsClientId, legacyCommwebServiceBaseUrl, monitoringSessionId, renewAccessTokenInterval } = options;
    if (!monitoringSessionId) {
        monitoringSessionId = (0, uuid_1.v4)();
    }
    if (!renewAccessTokenInterval) {
        renewAccessTokenInterval = 300000;
    }
    // Authentication service data
    const authenticationResult = (0, vue_1.ref)();
    const loginCredentials = (0, vue_1.ref)();
    let activeLoginPromise = undefined;
    let renewAccessTokenHandle;
    let isStoringSession = false;
    // Axios instance for commercial web and LEDS authentication calls
    const client = axios_1.default.create({
        baseURL: '',
        timeout: (_a = options.timeout) !== null && _a !== void 0 ? _a : 70000,
        responseType: 'json',
        headers: {
            'Cache-Control': 'no-cache'
        }
    });
    /**
     * Forcefully expire the current session, if it exists.
     */
    function expireSession() {
        return __awaiter(this, void 0, void 0, function* () {
            yield (0, lifecycle_hooks_1.runCallbacks)('sessionExpired');
            yield logout();
        });
    }
    /**
     * Retrieves the corresponding access token for its type.
     */
    function getAccessToken(type) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (type === 'commweb') {
                return (_a = authenticationResult.value) === null || _a === void 0 ? void 0 : _a.commwebAccessToken;
            }
            else if (type === 'leds') {
                return (_b = authenticationResult.value) === null || _b === void 0 ? void 0 : _b.ledsAccessToken;
            }
            return undefined;
        });
    }
    /**
     * Gets the expiration timestamp for the requested access token type.
     * Unimplemented for now... we don't have good session handling.
     */
    function getAccessTokenExpiration(type) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (type === 'commweb' && ((_a = authenticationResult.value) === null || _a === void 0 ? void 0 : _a.commwebAccessTokenExpiry)) {
                return authenticationResult.value.commwebAccessTokenExpiry;
            }
            else if (type === 'leds' && ((_b = authenticationResult.value) === null || _b === void 0 ? void 0 : _b.ledsAccessTokenExpiry)) {
                return authenticationResult.value.ledsAccessTokenExpiry;
            }
            return undefined;
        });
    }
    function getBaseUrls() {
        return __awaiter(this, void 0, void 0, function* () {
            return {
                commwebServiceBaseUrl,
                ledsServiceBaseUrl,
                legacyCommwebServiceBaseUrl
            };
        });
    }
    /**
     * Gets the ID that identifies the client for service calls (currently LEDS only).
     */
    function getClientId(type) {
        return __awaiter(this, void 0, void 0, function* () {
            if (type === 'commweb') {
                return undefined;
            }
            else if (type === 'leds') {
                return ledsClientId;
            }
            return undefined;
        });
    }
    /**
     * Gets the constituent (member/provider/etc.) the user has authenticated as.
     */
    function getConstituent() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = loginCredentials.value) === null || _a === void 0 ? void 0 : _a.constituent;
        });
    }
    /**
     * Retrieves the host region that we are pulling data from.
     */
    function getHostRegion() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            return (_a = authenticationResult.value) === null || _a === void 0 ? void 0 : _a.hostRegion;
        });
    }
    /**
     * Retrieves the unique ID used to identify a user for a monitoring session.
     */
    function getMonitoringSessionId() {
        return __awaiter(this, void 0, void 0, function* () {
            return monitoringSessionId;
        });
    }
    /**
     * Retrieves the OAuth equivalent of a refresh token, if it exists.
     */
    function getRefreshToken(type) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (type === 'commweb') {
                return undefined;
            }
            else if (type === 'leds') {
                return (_a = authenticationResult.value) === null || _a === void 0 ? void 0 : _a.ledsAccessToken;
            }
            return undefined;
        });
    }
    /**
     * Returns if currently fully authenticated (if not, assume we need to call all auth APIs again).
     */
    function isAuthenticated() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (activeLoginPromise != null) {
                try {
                    yield activeLoginPromise;
                }
                catch (error) {
                    return false;
                }
            }
            return !!((_a = authenticationResult.value) === null || _a === void 0 ? void 0 : _a.commwebAccessToken);
        });
    }
    /**
     * Call all necessary APIs to authenticate in order to be able to call commercial web and LEDS APIs.
     */
    function login() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            // Ensure only one login attempt is made at a time, in case this function is called multiple times.
            if (activeLoginPromise) {
                return yield activeLoginPromise;
            }
            let resolveActiveLoginPromise = () => undefined;
            let rejectActiveLoginPromise = () => undefined;
            activeLoginPromise = new Promise((resolve, reject) => {
                resolveActiveLoginPromise = resolve;
                rejectActiveLoginPromise = reject;
            });
            activeLoginPromise.catch((error) => error);
            try {
                window.clearInterval(renewAccessTokenHandle);
                const credentials = loginCredentials.value;
                if (!credentials) {
                    throw new Error('[authentication-service] Credentials not provided.');
                }
                // Transforms the LtpaToken2 cookie into a SAML assertion.
                const samlAssertionResponse = yield client.get(`${legacyCommwebServiceBaseUrl}commweb-rest-web/myapi/${credentials.constituent}token/health`, {
                    withCredentials: true,
                    headers: Object.assign({}, (((_a = loginCredentials.value) === null || _a === void 0 ? void 0 : _a.region) ? { 'X-Region': (_b = loginCredentials.value) === null || _b === void 0 ? void 0 : _b.region } : {}))
                });
                if (((_d = (_c = samlAssertionResponse === null || samlAssertionResponse === void 0 ? void 0 : samlAssertionResponse.data) === null || _c === void 0 ? void 0 : _c.status) !== null && _d !== void 0 ? _d : '').toUpperCase() !== 'SUCCESS') {
                    throw new Error('[authentication-service] During login, SAML assertion request failed.');
                }
                const samlAssertionToken = samlAssertionResponse.data.token;
                const hostRegion = samlAssertionResponse.data.region;
                // Transforms the SAML assertion into a JWT token that is used to authenticate
                // with commercial web microservices, as well as LEDS.
                const jwtRequestHeaders = {};
                if (credentials.region) {
                    jwtRequestHeaders['X-Region'] = credentials.region;
                }
                const commwebTokenResponse = yield client.post(`${commwebServiceBaseUrl}commservices-auth/api/v1/auth/member/jwt`, {
                    assertion: samlAssertionToken,
                    eventSessionId: credentials.eventSessionId
                }, {
                    headers: jwtRequestHeaders
                });
                // Build authentication result.
                let workingAuthenticationResult = {
                    commwebAccessToken: commwebTokenResponse.data.token,
                    commwebAccessTokenExpiry: 3093530677200000,
                    ledsAccessToken: '',
                    ledsAccessTokenExpiry: 0,
                    isSupportAccess: commwebTokenResponse.data.supportAccess,
                    eventUserType: commwebTokenResponse.data.eventUserType,
                    loggedInUserProfileName: commwebTokenResponse.data.loggedInUserProfileName,
                    supportAccessRole: commwebTokenResponse.data.supportAccessRole,
                    supportedProfileName: commwebTokenResponse.data.supportedProfileName,
                    hostRegion
                };
                // Allows member application to call additional APIs that do not apply to provider.
                const enhancedAuthenticationResult = yield (0, lifecycle_hooks_1.runCallbacks)('enhanceAuthenticationResult', workingAuthenticationResult);
                if (enhancedAuthenticationResult) {
                    workingAuthenticationResult = enhancedAuthenticationResult;
                }
                // Authenticate with LEDS. Don't know yet if other constituent apps like provider will use LEDS.
                // Hopefully this can be done in a more generic way when the project comes...
                if (credentials.constituent === 'member') {
                    // Retrieve a saml assertion to pass to LEDS
                    const ledsSamlAssertionResponse = yield client.get(`${commwebServiceBaseUrl}commservices-business-member/api/v1/member/apitoken`, {
                        headers: {
                            'Authorization': `Bearer ${workingAuthenticationResult.commwebAccessToken}`
                        }
                    });
                    // Authenticate with LEDS, get back session token (which we will call an access token)
                    const ledsAuthenticateRequestHeaders = {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json;charset=UTF-8',
                        'X-IBM-Client-ID': ledsClientId !== null && ledsClientId !== void 0 ? ledsClientId : '',
                        'X-Environment': legacyCommwebServiceBaseUrl.replace('https://', '')
                    };
                    if (workingAuthenticationResult.hostRegion) {
                        ledsAuthenticateRequestHeaders['X-Region'] = workingAuthenticationResult.hostRegion;
                    }
                    const ledsAuthenticateResponse = yield client.post(`${ledsServiceBaseUrl}member/authenticate`, {
                        samlAssertion: ledsSamlAssertionResponse.data.assertion
                    }, {
                        headers: ledsAuthenticateRequestHeaders
                    });
                    workingAuthenticationResult.ledsAccessToken = ledsAuthenticateResponse.data.sessionToken;
                    clearInterval(renewAccessTokenHandle);
                    renewAccessTokenHandle = window.setInterval(renewAccessToken, renewAccessTokenInterval);
                    workingAuthenticationResult.ledsAccessTokenExpiry
                        = new Date().getTime() + (renewAccessTokenInterval !== null && renewAccessTokenInterval !== void 0 ? renewAccessTokenInterval : 0);
                }
                authenticationResult.value = workingAuthenticationResult;
                if (isStoringSession) {
                    yield storeSession();
                }
                resolveActiveLoginPromise();
                activeLoginPromise = undefined;
            }
            catch (error) {
                rejectActiveLoginPromise(error);
                activeLoginPromise = undefined;
                throw error;
            }
        });
    }
    /**
     * Logs out.
     */
    function logout() {
        return __awaiter(this, void 0, void 0, function* () {
            window.clearInterval(renewAccessTokenHandle);
            deleteSessionStore();
            authenticationResult.value = undefined;
            yield (0, lifecycle_hooks_1.runCallbacks)('logout');
        });
    }
    /**
     * Attempt to refresh all logins when it looks like a session may have expired.
     */
    function refreshSession() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield login();
            }
            catch (loginError) {
                yield (0, lifecycle_hooks_1.runCallbacks)('sessionExpired');
                yield logout();
                throw loginError;
            }
        });
    }
    /**
     * For LEDS only right now, refreshes the equivalent of an OAuth2 access token (sessionToken).
     */
    function renewAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            if (authenticationResult.value) {
                try {
                    const ledsRenewRequestHeaders = {
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${authenticationResult.value.ledsAccessToken}`,
                        'Content-Type': 'application/json;charset=UTF-8',
                        'X-IBM-Client-ID': ledsClientId !== null && ledsClientId !== void 0 ? ledsClientId : '',
                        'X-Environment': legacyCommwebServiceBaseUrl.replace('https://', '')
                    };
                    if (authenticationResult.value.hostRegion) {
                        ledsRenewRequestHeaders['X-Region'] = authenticationResult.value.hostRegion;
                    }
                    const response = yield client.get(`${ledsServiceBaseUrl}member/renewSession`, {
                        headers: ledsRenewRequestHeaders
                    });
                    authenticationResult.value.ledsAccessToken = response.data.sessionToken;
                }
                catch (error) {
                    clearInterval(renewAccessTokenHandle);
                    yield refreshSession();
                    clearInterval(renewAccessTokenHandle);
                    renewAccessTokenHandle = window.setInterval(renewAccessToken, renewAccessTokenInterval);
                }
                // Refresh session could have blown this away.
                if (authenticationResult.value) {
                    authenticationResult.value.ledsAccessTokenExpiry
                        = new Date().getTime() + (renewAccessTokenInterval !== null && renewAccessTokenInterval !== void 0 ? renewAccessTokenInterval : 0);
                }
                if (isStoringSession) {
                    yield storeSession();
                }
            }
            else {
                clearInterval(renewAccessTokenHandle);
            }
        });
    }
    /**
     * Clears the session from session storage. Does not impact anything else. Login state will continue in memory.
     */
    function deleteSessionStore() {
        return __awaiter(this, void 0, void 0, function* () {
            isStoringSession = false;
            resetSessionStorage();
        });
    }
    /**
     * Restores the previous session from browser session storage
     */
    function restoreSession() {
        return __awaiter(this, void 0, void 0, function* () {
            isStoringSession = true;
            const storedAuthenticationResult = getFromSessionStorage('authenticationResult');
            if (storedAuthenticationResult) {
                authenticationResult.value = storedAuthenticationResult;
                commwebServiceBaseUrl = getFromSessionStorage('commwebServiceBaseUrl');
                ledsServiceBaseUrl = getFromSessionStorage('ledsServiceBaseUrl');
                ledsClientId = getFromSessionStorage('ledsClientId');
                ledsServiceBaseUrl = getFromSessionStorage('ledsServiceBaseUrl');
                legacyCommwebServiceBaseUrl = getFromSessionStorage('legacyCommwebServiceBaseUrl');
                renewAccessTokenInterval = getFromSessionStorage('renewAccessTokenInterval');
                client.defaults.timeout = getFromSessionStorage('timeout');
                loginCredentials.value = getFromSessionStorage('loginCredentials');
                clearInterval(renewAccessTokenHandle);
                renewAccessTokenHandle = window.setInterval(renewAccessToken, renewAccessTokenInterval);
            }
            else {
                deleteSessionStore();
                throw new Error('Data not found in session storage.');
            }
        });
    }
    /**
     * Stores the current session in browser session storage
     */
    function storeSession() {
        return __awaiter(this, void 0, void 0, function* () {
            isStoringSession = true;
            try {
                storeInSessionStorage('commwebServiceBaseUrl', commwebServiceBaseUrl);
                storeInSessionStorage('ledsServiceBaseUrl', ledsServiceBaseUrl);
                storeInSessionStorage('ledsClientId', ledsClientId);
                storeInSessionStorage('ledsServiceBaseUrl', ledsServiceBaseUrl);
                storeInSessionStorage('legacyCommwebServiceBaseUrl', legacyCommwebServiceBaseUrl);
                storeInSessionStorage('renewAccessTokenInterval', renewAccessTokenInterval);
                storeInSessionStorage('timeout', client.defaults.timeout);
                storeInSessionStorage('authenticationResult', authenticationResult.value);
                storeInSessionStorage('loginCredentials', loginCredentials.value);
            }
            catch (error) {
                /* istanbul ignore next */
                deleteSessionStore();
            }
        });
    }
    /**
     * Provide credential information used for logging in.
     */
    function useCredentials(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            loginCredentials.value = credentials;
            if (isStoringSession) {
                yield storeSession();
            }
        });
    }
    /**
     * Update the options used when creating the service
     */
    function useOptions(options) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (options.commwebServiceBaseUrl) {
                commwebServiceBaseUrl = options.commwebServiceBaseUrl;
            }
            if (options.ledsServiceBaseUrl) {
                ledsServiceBaseUrl = options.ledsServiceBaseUrl;
            }
            if (options.ledsClientId) {
                ledsClientId = options.ledsClientId;
            }
            if (options.legacyCommwebServiceBaseUrl) {
                legacyCommwebServiceBaseUrl = options.legacyCommwebServiceBaseUrl;
            }
            if (options.monitoringSessionId) {
                monitoringSessionId = options.monitoringSessionId;
            }
            if (options.timeout) {
                client.defaults.timeout = options.timeout;
            }
            if (options.renewAccessTokenInterval) {
                renewAccessTokenInterval = (_a = options.renewAccessTokenInterval) !== null && _a !== void 0 ? _a : 30000;
            }
            if (isStoringSession) {
                yield storeSession();
            }
        });
    }
    // Build the plugin object.
    const authenticationService = {
        client,
        deleteSessionStore,
        expireSession,
        getAccessToken,
        getAccessTokenExpiration,
        getBaseUrls,
        getClientId,
        getConstituent,
        getHostRegion,
        getMonitoringSessionId,
        getRefreshToken,
        isAuthenticated,
        login,
        logout,
        refreshSession,
        restoreSession,
        storeSession,
        useCredentials,
        useOptions,
        install(app) {
            app.provide(injection_symbols_1.authenticationServiceKey, authenticationService);
            app.provide(injection_symbols_1.authenticationServiceClientKey, client);
        }
    };
    return authenticationService;
}
exports.createAuthenticationService = createAuthenticationService;
//# sourceMappingURL=authentication-service.js.map